import { EVENT_GENDERS, type EventGender, MEDALS, type MedalTallyRow } from "@seagames/contracts";
import { Select } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { errorMessage } from "../../lib/api/messages";
import { progressQuery } from "../../lib/desk/queries";
import { gamesSportsQuery, useCurrentGames } from "../../lib/games";
import { formatCalendarDay, formatDateTime, formatInteger } from "../../lib/i18n/format";
import { type BoardTallyParams, boardDailyQuery, boardTallyQuery } from "../../lib/medals/board";
import { addDays } from "../../lib/medals/days";
import { type TallySort, cellOf, sharedRanks, sortTally, tallyCsv, tallyLeaders, tallyTotals } from "../../lib/medals/tally";
import { ChartFrame, ChartTable } from "../charts/chart-frame";
import { HeatKey, Heatmap } from "../charts/heatmap";
import { Legend } from "../charts/legend";
import { StackedBars } from "../charts/stacked-bar";
import { TipRow } from "../charts/tooltip";
import { MEDAL_COLOR, MedalBadge } from "../medals/medal-chip";
import { NocLabel } from "../medals/noc-label";
import { ProgressBySport } from "../medals/progress";
import { Card } from "../page/card";
import { PageHeader } from "../page/page-header";
import { Button } from "../ui/button";
import { EmptyState } from "../ui/empty-state";
import { Sheet } from "../ui/sheet";
import { Spinner } from "../ui/spinner";
import { ContingentDrawer } from "./contingent-drawer";
import { TallyTable } from "./tally-table";
import { useTvMode } from "./use-tv-mode";

export const TALLY_VIEWS = ["table", "bars", "heatmap"] as const;
export type TallyView = (typeof TALLY_VIEWS)[number];

export interface TallySearch {
	view?: TallyView | undefined;
	sport?: string | undefined;
	gender?: EventGender | undefined;
	/** Games days, both INCLUSIVE in the URL (the API's `to` is exclusive: `until + 1`). */
	from?: string | undefined;
	until?: string | undefined;
	sort?: TallySort | undefined;
	/** The NOC whose drawer is open. */
	noc?: string | undefined;
}

/** In TV mode the views take turns this often (when rotation is on). */
const TV_ROTATE_MS = 20_000;
const TV_POLL_MS = 30_000;

/**
 * The medal tally — `GET /medals/tally`, ONE cached aggregate per variant:
 * the day breakdown always (the table's sparklines, the drawer's columns),
 * the sport breakdown when the heatmap or a drawer needs it. Filters (sport,
 * gender, a range of games days — medals AWARDED on those days) and the view
 * live in the URL. The API's rank is the standing; a colour sort reorders.
 * Export CSV is built from the rows on screen; Print has its own sheet; TV
 * mode is the container fullscreen with larger type and a 30 s refresh.
 */
export function TallyPage({ search, navigate }: { search: TallySearch; navigate: (patch: Partial<TallySearch>) => void }) {
	const { t } = useTranslation("tally");
	const tm = useTranslation("medals").t;
	const tc = useTranslation("charts").t;
	const { gamesId, games, isLoading } = useCurrentGames();
	const enabled = gamesId !== null;
	const container = useRef<HTMLDivElement>(null);
	const tv = useTvMode(container);
	const [rotate, setRotate] = useState(false);
	const [tvView, setTvView] = useState<TallyView>("table");
	const view: TallyView = tv.on && rotate ? tvView : (search.view ?? "table");
	const sort: TallySort = search.sort ?? "rank";

	useEffect(() => {
		if (!tv.on || !rotate) return;
		setTvView(search.view ?? "table");
		const timer = setInterval(() => setTvView((v) => TALLY_VIEWS[(TALLY_VIEWS.indexOf(v) + 1) % TALLY_VIEWS.length] ?? "table"), TV_ROTATE_MS);
		return () => clearInterval(timer);
	}, [tv.on, rotate, search.view]);

	const base: BoardTallyParams = {
		games_id: gamesId ?? "",
		sport_id: search.sport,
		gender: search.gender,
		from: search.from,
		to: search.until ? addDays(search.until, 1) : undefined,
	};
	const poll = tv.on ? TV_POLL_MS : undefined;
	const byDay = useQuery({ ...boardTallyQuery({ ...base, breakdown: "day" }), enabled, ...(poll ? { refetchInterval: poll } : {}) });
	const bySport = useQuery({ ...boardTallyQuery({ ...base, breakdown: "sport" }), enabled: enabled && (view === "heatmap" || search.noc !== undefined), ...(poll ? { refetchInterval: poll } : {}) });
	const sports = useQuery({ ...gamesSportsQuery(gamesId ?? ""), enabled });
	const daily = useQuery({ ...boardDailyQuery(gamesId ?? ""), enabled });
	const progress = useQuery({ ...progressQuery(gamesId ?? ""), enabled });

	if (!gamesId) {
		return isLoading ? <Spinner className="size-[1.5rem] text-board" /> : <EmptyState title={tm("noGames.title")} body={tm("noGames.body")} />;
	}

	const rows = sortTally(byDay.data?.rows ?? [], sort);
	const shared = sharedRanks(rows);
	const totals = tallyTotals(rows);
	const leaders = tallyLeaders(rows);
	const calendar = (daily.data?.days ?? []).map((d) => d.day).filter((d) => (!search.from || d >= search.from) && (!search.until || d <= search.until));
	// The sparklines run over the calendar AND every day a counted medal was awarded (the day breakdown's columns), so no medal falls outside them.
	const trendDays = [...new Set([...calendar, ...(byDay.data?.columns ?? []).map((c) => c.key)])].sort();
	const open = search.noc ? rows.find((r) => r.noc.responsible_organization_id === search.noc) : undefined;
	const sportName = sports.data?.find((s) => s.sport_id === search.sport)?.name;
	const filtered = search.sport !== undefined || search.gender !== undefined || search.from !== undefined || search.until !== undefined;
	const scope = [
		sportName,
		search.gender ? t(`gender.${search.gender}`) : undefined,
		search.from || search.until
			? t("range", { from: formatCalendarDay(search.from ?? calendar[0] ?? ""), to: formatCalendarDay(search.until ?? calendar[calendar.length - 1] ?? "") })
			: undefined,
	]
		.filter(Boolean)
		.join(" · ");
	const asOf = byDay.dataUpdatedAt > 0 ? new Date(byDay.dataUpdatedAt) : new Date();
	const decided = progress.data
		? search.sport
			? progress.data.sports.find((s) => s.sport_id === search.sport)
			: progress.data.totals
		: undefined;

	const exportCsv = () => {
		const text = tallyCsv(rows, {
			heading: [t("title"), games?.name ?? "", ...(scope ? [scope] : []), t("asOf", { time: formatDateTime(asOf) })],
			head: [t("table.rank"), t("csv.code"), t("table.noc"), tm("medal.GOLD"), tm("medal.SILVER"), tm("medal.BRONZE"), t("table.total")],
			shared,
		});
		const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
		const a = document.createElement("a");
		a.href = url;
		a.download = `medal-tally-${games?.code ?? "games"}-${asOf.toISOString().slice(0, 10)}.csv`;
		document.body.appendChild(a);
		a.click();
		a.remove();
		URL.revokeObjectURL(url);
	};

	const sportRow = (r: MedalTallyRow) => bySport.data?.rows.find((x) => x.noc.responsible_organization_id === r.noc.responsible_organization_id);
	const heatColumns = (bySport.data?.columns ?? []).filter((c) => (bySport.data?.rows ?? []).some((r) => cellOf(r, c.key).total > 0));

	return (
		<>
			<PageHeader title={sportName ? tm("tally.titleInSport", { sport: sportName }) : t("title")} subtitle={t("subtitle", { games: games?.name ?? "" })} className="print:hidden" />

			<div ref={container} className={`mses-tv flex flex-col gap-[1rem] ${tv.window ? "fixed inset-0 z-[80] overflow-auto bg-paper px-[2.5rem] py-[1.5rem]" : ""}`}>
				{/* The printed sheet's heading (and TV mode's title): what, which games, which filters, as of when. */}
				<div className={`${tv.on ? "flex" : "hidden print:flex"} flex-wrap items-end justify-between gap-[0.5rem] border-ink border-b-2 pb-[0.5rem]`}>
					<span className="flex flex-col">
						<span className="font-black text-[1.5rem] text-ink">{t("title")}</span>
						<span className="text-[0.875rem] text-muted">{[games?.name, scope].filter(Boolean).join(" · ")}</span>
					</span>
					<span className="text-[0.875rem] text-muted">{t("asOf", { time: formatDateTime(asOf) })}</span>
				</div>

				<Card className={`flex flex-col gap-[0.75rem] px-[1rem] py-[0.875rem] print:hidden ${tv.on ? "hidden" : ""}`}>
					<div className="flex flex-wrap items-center gap-[0.5rem]">
						<div role="radiogroup" aria-label={t("view.label")} className="inline-flex rounded-btn border border-rule bg-paper p-[0.1875rem]">
							{TALLY_VIEWS.map((v) => (
								<button
									key={v}
									type="button"
									role="radio"
									aria-checked={view === v}
									onClick={() => navigate({ view: v === "table" ? undefined : v })}
									className={`min-h-[2.25rem] rounded-[0.375rem] px-[0.875rem] font-bold text-[0.8125rem] pointer-coarse:min-h-touch ${view === v ? "bg-board text-white" : "text-ink hover:bg-sheet"}`}
								>
									{t(`view.${v}`)}
								</button>
							))}
						</div>
						<span className="ml-auto flex flex-wrap gap-[0.5rem]">
							<Button size="sm" variant="outline" onClick={exportCsv} disabled={rows.length === 0}>
								{t("actions.csv")}
							</Button>
							<Button size="sm" variant="outline" onClick={() => window.print()}>
								{t("actions.print")}
							</Button>
							<Button size="sm" onClick={tv.enter}>
								{t("actions.tv")}
							</Button>
						</span>
					</div>
					<div className="flex flex-wrap items-end gap-[0.5rem]">
						<Select
							size="sm"
							className="w-full sm:w-[13rem]"
							aria-label={tm("filters.sport")}
							options={(sports.data ?? []).map((s) => ({ value: s.sport_id, label: s.name }))}
							value={search.sport ?? null}
							onChange={(v) => navigate({ sport: v ?? undefined })}
							placeholder={tm("filters.allSports")}
							clearable
						/>
						<label className="flex flex-col gap-[0.125rem] text-[0.75rem] text-muted">
							{t("filters.gender")}
							<select
								value={search.gender ?? ""}
								onChange={(e) => navigate({ gender: (e.target.value || undefined) as EventGender | undefined })}
								className="min-h-[2.5rem] rounded-btn border border-rule bg-sheet px-[0.5rem] text-[0.875rem] text-ink focus:border-board"
							>
								<option value="">{t("gender.all")}</option>
								{EVENT_GENDERS.map((g) => (
									<option key={g} value={g}>
										{t(`gender.${g}`)}
									</option>
								))}
							</select>
						</label>
						<label className="flex flex-col gap-[0.125rem] text-[0.75rem] text-muted">
							{t("filters.from")}
							<input
								type="date"
								value={search.from ?? ""}
								min={calendar[0]}
								max={search.until ?? daily.data?.days[daily.data.days.length - 1]?.day}
								onChange={(e) => navigate({ from: e.target.value || undefined })}
								className="min-h-[2.5rem] rounded-btn border border-rule bg-sheet px-[0.5rem] text-[0.875rem] text-ink focus:border-board"
							/>
						</label>
						<label className="flex flex-col gap-[0.125rem] text-[0.75rem] text-muted">
							{t("filters.until")}
							<input
								type="date"
								value={search.until ?? ""}
								min={search.from ?? daily.data?.days[0]?.day}
								onChange={(e) => navigate({ until: e.target.value || undefined })}
								className="min-h-[2.5rem] rounded-btn border border-rule bg-sheet px-[0.5rem] text-[0.875rem] text-ink focus:border-board"
							/>
						</label>
						{daily.data && (
							<Button size="sm" variant="quiet" onClick={() => navigate({ from: daily.data.today, until: daily.data.today })}>
								{t("filters.today")}
							</Button>
						)}
						{filtered && (
							<Button size="sm" variant="quiet" onClick={() => navigate({ sport: undefined, gender: undefined, from: undefined, until: undefined })}>
								{t("filters.clear")}
							</Button>
						)}
					</div>
				</Card>

				<section aria-label={t("summary.label")} className="grid grid-cols-1 gap-[0.75rem] sm:grid-cols-2 xl:grid-cols-4">
					<Card className="flex flex-col gap-[0.25rem] px-[1rem] py-[0.75rem]">
						<span className="font-bold text-[0.75rem] text-muted uppercase tracking-wide">{leaders.length > 1 ? t("summary.tied") : t("summary.leader")}</span>
						{leaders.length === 0 ? (
							<span className="text-[0.9375rem] text-muted">{t("summary.noLeader")}</span>
						) : (
							leaders.map((r) => <NocLabel key={r.noc.responsible_organization_id} noc={r.noc} className="font-black text-[1.125rem] text-ink" />)
						)}
					</Card>
					<Card className="flex flex-col gap-[0.25rem] px-[1rem] py-[0.75rem]">
						<span className="font-bold text-[0.75rem] text-muted uppercase tracking-wide">{t("summary.medals")}</span>
						<span className="font-black text-[1.5rem] text-ink leading-tight">{formatInteger(totals.total)}</span>
						<span className="flex gap-[0.75rem] font-mono text-[0.875rem]">
							{MEDALS.map((m) => (
								<span key={m} className="inline-flex items-center gap-[0.25rem]">
									<MedalBadge medal={m} className="size-[1rem] text-[0.5625rem]" />
									{formatInteger(m === "GOLD" ? totals.gold : m === "SILVER" ? totals.silver : totals.bronze)}
								</span>
							))}
						</span>
					</Card>
					<Card className="flex flex-col gap-[0.25rem] px-[1rem] py-[0.75rem]">
						<span className="font-bold text-[0.75rem] text-muted uppercase tracking-wide">{t("summary.nocs")}</span>
						<span className="font-black text-[1.5rem] text-ink leading-tight">
							{t("summary.ofTotal", { n: formatInteger(rows.filter((r) => r.total > 0).length), total: formatInteger(rows.length) })}
						</span>
						<span className="text-[0.8125rem] text-muted">{t("summary.nocsDetail")}</span>
					</Card>
					<Card className="flex flex-col gap-[0.25rem] px-[1rem] py-[0.75rem]">
						<span className="font-bold text-[0.75rem] text-muted uppercase tracking-wide">{t("summary.events")}</span>
						<span className="font-black text-[1.5rem] text-ink leading-tight">
							{decided ? t("summary.ofTotal", { n: formatInteger(decided.awarded + decided.no_medals), total: formatInteger(decided.events) }) : "—"}
						</span>
						<span className="text-[0.8125rem] text-muted">{search.sport ? t("summary.eventsSport") : t("summary.eventsAll")}</span>
					</Card>
				</section>

				<Card className="px-[1rem] py-[0.875rem]">
					{byDay.isPending ? (
						<Spinner className="size-[1.25rem] text-board" />
					) : byDay.isError ? (
						<p className="text-conflict">{errorMessage(byDay.error)}</p>
					) : rows.length === 0 ? (
						<p className="text-[0.875rem] text-muted">{tm("tally.empty")}</p>
					) : view === "table" ? (
						<TallyTable
							rows={rows}
							shared={shared}
							days={trendDays}
							sort={sort}
							onSort={(s) => navigate({ sort: s === "rank" ? undefined : s })}
							onOpen={(r) => navigate({ noc: r.noc.responsible_organization_id })}
						/>
					) : view === "bars" ? (
						<ChartFrame
							interactive
							title={t("bars.title")}
							description={t("bars.description")}
							legend={<Legend items={MEDALS.map((m) => ({ key: m, label: tm(`medal.${m}`), color: MEDAL_COLOR[m] }))} />}
							table={
								<ChartTable
									head={[t("table.noc"), tm("medal.GOLD"), tm("medal.SILVER"), tm("medal.BRONZE"), t("table.total")]}
									rows={rows.map((r) => [r.noc.code ?? r.noc.name, formatInteger(r.gold), formatInteger(r.silver), formatInteger(r.bronze), formatInteger(r.total)])}
								/>
							}
						>
							<StackedBars
								labelWidth="13rem"
								rows={rows.map((r) => ({
									key: r.noc.responsible_organization_id,
									title: `${r.noc.code ?? ""} ${r.noc.country_name ?? r.noc.name}`.trim(),
									label: (
										<button type="button" onClick={() => navigate({ noc: r.noc.responsible_organization_id })} className="flex min-w-0 items-center gap-[0.5rem] text-ink hover:underline">
											<span className="w-[1.75rem] shrink-0 text-right font-mono text-[0.75rem] text-muted">
												{shared.has(r.rank) ? `=${r.rank}` : r.rank}
											</span>
											<NocLabel noc={r.noc} />
										</button>
									),
									segments: MEDALS.map((m) => ({ key: m, label: tm(`medal.${m}`), color: MEDAL_COLOR[m], value: m === "GOLD" ? r.gold : m === "SILVER" ? r.silver : r.bronze })),
								}))}
							/>
						</ChartFrame>
					) : bySport.isPending ? (
						<Spinner className="size-[1.25rem] text-board" />
					) : bySport.isError ? (
						<p className="text-conflict">{errorMessage(bySport.error)}</p>
					) : heatColumns.length === 0 ? (
						<p className="text-[0.875rem] text-muted">{tm("tally.empty")}</p>
					) : (
						<ChartFrame interactive title={t("heatmap.title")} description={t("heatmap.description")} legend={<HeatKey few={tc("heat.few")} many={tc("heat.many")} />}>
							<Heatmap
								corner={t("table.noc")}
								totalLabel={t("table.total")}
								columns={heatColumns.map((c) => ({ key: c.key, label: c.code, title: c.name }))}
								rows={sortTally(bySport.data?.rows ?? [], sort).map((r) => ({
									key: r.noc.responsible_organization_id,
									title: r.noc.code ?? r.noc.name,
									total: r.total,
									values: new Map((r.cells ?? []).map((c) => [c.key, c.total])),
									tip: (k) => {
										const c = cellOf(r, k);
										return (
											<>
												{MEDALS.map((m) => (
													<TipRow key={m} color={MEDAL_COLOR[m]} label={tm(`medal.${m}`)} value={formatInteger(m === "GOLD" ? c.gold : m === "SILVER" ? c.silver : c.bronze)} />
												))}
											</>
										);
									},
									header: (
										<button type="button" onClick={() => navigate({ noc: r.noc.responsible_organization_id })} className="text-ink hover:underline">
											<NocLabel noc={r.noc} showName={false} />
										</button>
									),
								}))}
							/>
						</ChartFrame>
					)}
				</Card>

				{tv.on && (
					<div className="flex flex-wrap items-center justify-between gap-[0.75rem] text-[0.875rem] text-muted">
						<label className="flex items-center gap-[0.5rem]">
							<input type="checkbox" checked={rotate} onChange={(e) => setRotate(e.target.checked)} className="size-[1.125rem]" />
							{t("tv.rotate")}
						</label>
						<span>{t("tv.hint")}</span>
						<Button size="sm" variant="outline" onClick={tv.exit}>
							{t("tv.exit")}
						</Button>
					</div>
				)}
			</div>

			<div className="print:hidden">
				<ProgressBySport gamesId={gamesId} sportId={search.sport} />
			</div>

			<Sheet
				open={open !== undefined}
				onClose={() => navigate({ noc: undefined })}
				title={open ? <NocLabel noc={open.noc} /> : ""}
				subtitle={[games?.name, scope].filter(Boolean).join(" · ")}
			>
				{open && (
					<ContingentDrawer
						gamesId={gamesId}
						sportId={search.sport}
						row={open}
						ofRows={rows.length}
						shared={shared.has(open.rank)}
						sportCells={bySport.data ? sportRow(open) : undefined}
						sportColumns={bySport.data?.columns ?? []}
						dayCells={byDay.data?.rows.find((x) => x.noc.responsible_organization_id === open.noc.responsible_organization_id)}
						dayColumns={byDay.data?.columns ?? []}
					/>
				)}
			</Sheet>
		</>
	);
}
